import { get_upload_full_url } from "@/app/network/endpoints/api-endpoints";
import { BlogAuthorFaisaluddin } from "@/lib/images";
import Image from "next/image";

interface TeamMember {
    uuid: string;
    f_name: string;
    l_name: string;
    email: string;
    phone: string;
    department: string;
    designation: string;
    profile_image: string;
    description: string;
    linkedin_url?: string;
    twitter_url?: string;
    github_url?: string;
    skills: string;
}

interface UserCardProps {
    member: TeamMember;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

export default function UserCard({ member, isExpanded, onToggleExpand }: UserCardProps) {
    const fullName = `${member.f_name} ${member.l_name}`;
    const profileImageUrl = member.profile_image
        ? get_upload_full_url('user_profile_image', member.profile_image)
        : BlogAuthorFaisaluddin.src;

    const description = member.description;
    const shouldShowReadMore = description?.length > 150; // Show read more if description is longer than 150 characters
    const displayText = isExpanded ? description : description?.slice(0, 150) + (shouldShowReadMore ? '...' : '');

    return (
        <div className="group">
            <div className="flex flex-col bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 h-full hover:border-indigo-200">
                {/* Profile Image */}
                <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-4">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-3 border-gray-100 group-hover:border-indigo-200 transition-colors duration-300 shadow-lg">
                            <Image
                                src={profileImageUrl}
                                alt={fullName}
                                className="w-full h-full object-cover object-top"
                                width={96}
                                height={96}
                            />
                        </div>
                    </div>

                    {/* Name and Designation */}
                    <div className="text-center">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                            {fullName}
                        </h4>
                        <div className="mb-3">
                            <span className="group relative cursor-pointer overflow-hidden bg-radial-[at_bottom_right] from-[#100588] to-[#5231E7] to-60% rounded-full border-2 outline-white px-3 py-1 text-sm font-semibold text-white shadow-[0px_2px_6px_0px_rgba(82,49,231,0.30)] hover:scale-[1.02] transition-transform duration-300">
                                {/* Shimmer Effect */}
                                <span
                                    className="absolute inset-0 w-full h-full"
                                    style={{
                                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                                        transform: "translateX(-100%)",
                                        animation: "shimmer 2.5s infinite"
                                    }}
                                />
                                <span className="relative z-10">{member.designation}</span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                    {/* Description with Read More */}
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 leading-relaxed text-center">
                            {displayText}
                        </p>
                        {shouldShowReadMore && (
                            <button
                                onClick={onToggleExpand}
                                className="text-[#5231E7] text-xs font-medium hover:text-[#764AF1] transition-colors duration-200 mt-2 mx-auto block"
                            >
                                {isExpanded ? 'Read less' : 'Read more'}
                            </button>
                        )}
                    </div>

                    {/* Skills */}
                    {member.skills && (
                        <div>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {member.skills.split(',').map((skill, index) => (
                                    <span
                                        key={index}
                                        className="px-2 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-md border border-gray-200"
                                    >
                                        {skill.trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Social Links */}
                <div className="flex justify-center gap-3 pt-6 mt-6 border-t border-gray-100">
                    {member.linkedin_url && (
                        <a
                            href={member.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 text-gray-600 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                        </a>
                    )}
                    {member.twitter_url && (
                        <a
                            href={member.twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-gray-50 hover:bg-sky-50 hover:text-sky-600 text-gray-600 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </a>
                    )}
                    {member.github_url && (
                        <a
                            href={member.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-gray-50 hover:bg-gray-100 hover:text-gray-800 text-gray-600 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                            </svg>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
} 